import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { PegaState } from '../journey/pega/apiGetPegaState';
import logger from '../utils/logger.ts';
import { setMessagingScope } from '../utils/pCoreWindow';

type PegaAppContext = {
  Product?: string;
  Platform?: string;
  CustomerType?: string;
  Context?: string;
  IsFromPlatform?: boolean;
  Channel?: string;
  MultiAsset?: string;
  IsCancelled?: boolean | string;
  ForceNoModal?: boolean | string;
  SumsubVerificationLink?: string;
};

interface PegaEventContextValue {
  initPegaSession: () => void;
  subscribeToPegaEvents: () => void;
  unsubscribeFromPegaEvents: () => void;
  subscribeSetAppContextForContext: (contextName: string, caseID?: string) => void;
  isEndOfAssignment: boolean;
  resetEndOfAssignment: () => void;
  resetAppContext: () => void;
  onPegaTokenExpiry: () => void;
  pegaState?: PegaState;
  setPegaState: (state: PegaState | undefined) => void;
  appContext: PegaAppContext;
}

const PegaEventContext = createContext<PegaEventContextValue | undefined>(undefined);

type PegaEventProviderProps = {
  onPegaTokenExpiry: () => void;
  children: ReactNode;
};

type UnsubscribeFn = () => void;

const logSetAppContext = (message: string, payload?: unknown) => {
  if (!import.meta.env.DEV && import.meta.env.VITE_CUSTOM_ENV !== 'nonprod') {
    return;
  }

  const serializedPayload = payload === undefined ? '' : JSON.stringify(payload);
  logger.info(`[CMC_CRM_SET_APP_CONTEXT] ${new Date().toISOString()} ${message} ${serializedPayload}`);
};

const waitForPCore = (): Promise<unknown> => {
  return new Promise((resolve) => {
    const checkIfPCoreIsAvailable = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const PCore = (window as any).PCore;

      if (PCore) {
        resolve(PCore);
        return;
      }

      setTimeout(checkIfPCoreIsAvailable, 100);
    };

    checkIfPCoreIsAvailable();
  });
};

const waitForMessagingServiceManager = (): Promise<{
  subscribe: (
    filter: { matcher: string; criteria?: Record<string, unknown> },
    handler: (payload: unknown) => void,
    contextName?: string
  ) => string;
  unsubscribe: (id: string) => void;
}> => {
  return new Promise((resolve) => {
    const checkIfMessagingServiceIsAvailable = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const PCore = (window as any).PCore;
      const messagingServiceManager = PCore?.getMessagingServiceManager?.();

      if (messagingServiceManager?.subscribe && messagingServiceManager?.unsubscribe) {
        resolve(messagingServiceManager);
        return;
      }

      setTimeout(checkIfMessagingServiceIsAvailable, 100);
    };

    checkIfMessagingServiceIsAvailable();
  });
};

const subscribeToCustomReauth = async (onPegaTokenExpiry: () => void): Promise<UnsubscribeFn | undefined> => {
  if (typeof window === 'undefined') return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const PCore = (await waitForPCore()) as any;

    PCore.getPubSubUtils().subscribe(
      window.PCore.getConstants().PUB_SUB_EVENTS.EVENT_CUSTOM_REAUTH,
      () => {
        logger.info('Pega token expired, redirecting back to login');
        onPegaTokenExpiry();
      },
      'customReauthSubscriptionKey'
    );

    return () => {
      PCore.getPubSubUtils().unsubscribe(
        PCore.getConstants().PUB_SUB_EVENTS.EVENT_CUSTOM_REAUTH,
        'customReauthSubscriptionKey'
      );
    };
  } catch (error) {
    logger.error(
      'Failed to subscribe to Pega customReauth event: ' + (error instanceof Error ? error.message : String(error))
    );
    return;
  }
};

const SET_APP_CONTEXT_MATCHER = 'CMC_CRM_SET_APP_CONTEXT';

let isSetAppContextActive = false;
let setAppContextUnsubscribe: UnsubscribeFn | undefined;
let currentMessagingContext: string | undefined;
let currentCaseID: string | undefined;
let applyAppContext: ((context: PegaAppContext) => void) | undefined;

const teardownSetAppContextSubscription = () => {
  isSetAppContextActive = false;

  if (!setAppContextUnsubscribe) {
    return;
  }

  const unsubscribe = setAppContextUnsubscribe;
  setAppContextUnsubscribe = undefined;
  currentMessagingContext = undefined;
  currentCaseID = undefined;
  logSetAppContext('unsubscribing');
  unsubscribe();
};

const getPayloadContext = (payload: unknown): string | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }

  const record = payload as Record<string, unknown>;
  const context = record.context ?? record.contextName ?? record.ContextName;
  return typeof context === 'string' && context.trim() !== '' ? context : undefined;
};

const getPayloadCaseID = (payload: unknown, eventMessage: PegaAppContext): string | undefined => {
  const sources = [payload, eventMessage];
  for (const source of sources) {
    if (!source || typeof source !== 'object') {
      continue;
    }

    const record = source as Record<string, unknown>;
    const nestedMessage = record.message;
    const nestedCriteria = record.criteria;
    const id =
      record.ID ??
      record.Id ??
      record.caseID ??
      record.CaseID ??
      (nestedCriteria && typeof nestedCriteria === 'object'
        ? (nestedCriteria as Record<string, unknown>).ID
        : undefined) ??
      (nestedMessage && typeof nestedMessage === 'object'
        ? ((nestedMessage as Record<string, unknown>).ID ??
          (nestedMessage as Record<string, unknown>).caseID ??
          (nestedMessage as Record<string, unknown>).CaseID)
        : undefined);

    if (typeof id === 'string' && id.trim() !== '') {
      return id;
    }
  }

  return undefined;
};

const caseIdsMatch = (expected: string, received: string): boolean => {
  return expected === received || received.includes(expected) || expected.includes(received);
};

const subscribeToSetAppContext = async (contextName: string, caseID: string): Promise<UnsubscribeFn | undefined> => {
  if (typeof window === 'undefined') return;

  try {
    logSetAppContext('waiting for messaging service');
    const messagingServiceManager = await waitForMessagingServiceManager();
    const messagingContext = contextName;

    if (currentMessagingContext === messagingContext && currentCaseID === caseID && setAppContextUnsubscribe) {
      return setAppContextUnsubscribe;
    }

    teardownSetAppContextSubscription();
    isSetAppContextActive = true;
    currentMessagingContext = messagingContext;
    currentCaseID = caseID;

    const filter = {
      matcher: SET_APP_CONTEXT_MATCHER,
      criteria: { ID: caseID },
    };
    logSetAppContext('subscribing', { filter, context: messagingContext });

    const subscriptionId = messagingServiceManager.subscribe(
      filter,
      (payload: unknown) => {
        logSetAppContext('received', payload);

        if (!isSetAppContextActive) {
          logSetAppContext('ignored (inactive)', payload);
          return;
        }

        const payloadContext = getPayloadContext(payload);
        const eventMessage =
          payload && typeof payload === 'object' && 'message' in payload
            ? ((payload as { message?: PegaAppContext }).message ?? {})
            : ((payload as PegaAppContext) ?? {});
        const payloadCaseID = getPayloadCaseID(payload, eventMessage);

        if (payloadContext && payloadContext !== messagingContext) {
          logSetAppContext('ignored (other context)', { messagingContext, payloadContext });
          return;
        }

        if (payloadCaseID && !caseIdsMatch(caseID, payloadCaseID)) {
          logSetAppContext('ignored (other case)', { caseID, payloadCaseID });
          return;
        }

        const {
          Product,
          Platform,
          CustomerType,
          Context,
          IsFromPlatform,
          Channel,
          MultiAsset,
          IsCancelled,
          ForceNoModal,
          SumsubVerificationLink,
        } = eventMessage;

        if (
          Product ||
          Platform ||
          CustomerType ||
          Context ||
          IsFromPlatform ||
          Channel ||
          MultiAsset ||
          IsCancelled !== undefined ||
          ForceNoModal !== undefined ||
          SumsubVerificationLink
        ) {
          const context = {
            ...(Product && { Product }),
            ...(Platform && { Platform }),
            ...(CustomerType && { CustomerType }),
            ...(Context && { Context }),
            ...(IsFromPlatform && { IsFromPlatform }),
            ...(Channel && { Channel }),
            ...(MultiAsset && { MultiAsset }),
            ...(IsCancelled !== undefined && { IsCancelled }),
            ...(ForceNoModal !== undefined && { ForceNoModal }),
            ...(SumsubVerificationLink && { SumsubVerificationLink }),
          };
          logSetAppContext('applying', context);
          applyAppContext?.(context);
        }
      },
      messagingContext
    );

    if (!subscriptionId) {
      isSetAppContextActive = false;
      currentMessagingContext = undefined;
      currentCaseID = undefined;
      return;
    }

    logSetAppContext('subscribed', { subscriptionId, context: messagingContext, caseID });

    setAppContextUnsubscribe = () => {
      isSetAppContextActive = false;
      currentMessagingContext = undefined;
      currentCaseID = undefined;
      messagingServiceManager.unsubscribe(subscriptionId);
      setAppContextUnsubscribe = undefined;
    };

    return setAppContextUnsubscribe;
  } catch (error) {
    isSetAppContextActive = false;
    currentMessagingContext = undefined;
    currentCaseID = undefined;
    logger.error(
      'Failed to subscribe to Pega CMC_CRM_SET_APP_CONTEXT message: ' +
        (error instanceof Error ? error.message : String(error))
    );
    return;
  }
};

const subscribeToEndOfAssignment = async (onEndOfAssignment: () => void): Promise<UnsubscribeFn | undefined> => {
  if (typeof window === 'undefined') return;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const PCore = (await waitForPCore()) as any;

    PCore.getPubSubUtils().subscribe(
      'endOfAssignmentProcessing',
      () => {
        onEndOfAssignment();
      },
      'endOfAssignmentProcessingSubscriptionKey'
    );

    return () => {
      PCore.getPubSubUtils().unsubscribe('endOfAssignmentProcessing', 'endOfAssignmentProcessingSubscriptionKey');
    };
  } catch (error) {
    logger.error(
      'Failed to subscribe to Pega endOfAssignmentProcessing event: ' +
        (error instanceof Error ? error.message : String(error))
    );
    return;
  }
};

export const PegaEventProvider = ({ onPegaTokenExpiry, children }: PegaEventProviderProps) => {
  const [isEndOfAssignment, setIsEndOfAssignment] = useState(false);
  const [pegaState, setPegaState] = useState<PegaState | undefined>();
  const [appContext, setAppContext] = useState<PegaAppContext>({});
  const unsubscribePegaEventFns = useRef<UnsubscribeFn[]>([]);
  const subscriptionGenerationRef = useRef(0);
  const unsubscribeFromPegaEventsRef = useRef<() => void>(() => {});

  applyAppContext = (context) => {
    setAppContext((prev) => ({ ...prev, ...context }));
  };

  const resetEndOfAssignment = useCallback(() => {
    setIsEndOfAssignment(false);
  }, []);

  const resetAppContext = useCallback(() => {
    setAppContext({});
  }, []);

  const unsubscribeFromPegaEvents = useCallback(() => {
    subscriptionGenerationRef.current += 1;
    teardownSetAppContextSubscription();
    unsubscribePegaEventFns.current.forEach((fn) => {
      fn();
    });
    unsubscribePegaEventFns.current = [];
  }, []);

  useEffect(() => {
    unsubscribeFromPegaEventsRef.current = unsubscribeFromPegaEvents;
  }, [unsubscribeFromPegaEvents]);

  const initPegaSession = useCallback(() => {
    if (window.PCore) {
      // Horrible hack we have to do to get pega to load after a token expiry, without it pega just hangs.
      // This is the solution provided to us by Labbs.
      // It's extra awful having to put it in a timeout else it's very flaky.
      // Hopefully the token expiring is an edge case though.
      setTimeout(() => {
        window.PCore?.getPubSubUtils().publish('updateSession', {
          updateStatus: 'success',
        });
      }, 500);
    }
  }, []);

  const subscribeToPegaEvents = useCallback(() => {
    if (unsubscribePegaEventFns.current.length > 0) {
      return;
    }

    const generation = subscriptionGenerationRef.current;

    const registerUnsubscribe = (unsubscribe: UnsubscribeFn | undefined) => {
      if (!unsubscribe) {
        return;
      }

      if (generation !== subscriptionGenerationRef.current) {
        unsubscribe();
        return;
      }

      unsubscribePegaEventFns.current.push(unsubscribe);
    };

    // Subscribe to 'customReauth' - this is so we can detect if the pega token expires
    subscribeToCustomReauth(onPegaTokenExpiry).then(registerUnsubscribe);

    // Subscribe to 'endOfAssignmentProcessing' - this is so we can show our own 'ThankYou' screen at the end
    subscribeToEndOfAssignment(() => {
      setIsEndOfAssignment(true);
      // Delay teardown to allow any final SET_APP_CONTEXT messages (e.g. SumsubVerificationLink) to arrive
      setTimeout(() => {
        teardownSetAppContextSubscription();
        unsubscribeFromPegaEventsRef.current();
      }, 2000);
    }).then(registerUnsubscribe);
  }, [onPegaTokenExpiry]);

  const subscribeSetAppContextForContext = useCallback((contextName: string, caseID?: string) => {
    if (!contextName || !caseID) {
      logSetAppContext('skipped (need pconnect context and case ID)', { contextName, caseID });
      return;
    }

    logSetAppContext('pconnect context', { contextName, caseID });
    setMessagingScope(contextName, caseID);
    void subscribeToSetAppContext(contextName, caseID);
  }, []);

  useEffect(() => {
    return () => {
      unsubscribeFromPegaEvents();
    };
  }, [unsubscribeFromPegaEvents]);

  const contextValue = useMemo(
    (): PegaEventContextValue => ({
      initPegaSession,
      subscribeToPegaEvents,
      unsubscribeFromPegaEvents,
      subscribeSetAppContextForContext,
      isEndOfAssignment,
      resetEndOfAssignment,
      resetAppContext,
      onPegaTokenExpiry,
      pegaState,
      setPegaState,
      appContext,
    }),
    [
      isEndOfAssignment,
      resetEndOfAssignment,
      resetAppContext,
      initPegaSession,
      subscribeToPegaEvents,
      unsubscribeFromPegaEvents,
      subscribeSetAppContextForContext,
      pegaState,
      onPegaTokenExpiry,
      appContext,
    ]
  );

  return <PegaEventContext.Provider value={contextValue}>{children}</PegaEventContext.Provider>;
};

export const usePegaEventsContext = () => {
  const context = useContext(PegaEventContext);
  if (context === undefined) {
    throw new Error('usePegaEvents must be used within PegaEventListener');
  }
  return context;
};
